/**
 * ProductSearchReporter — "Couldn't find a product" contextual link (T35.6.3).
 *
 * Proto: `reportMissingProduct(term, surface)` called from the product-search
 * no-result state. Opens the report sheet pre-seeded with the search term and
 * a productsearch item context. No severity — context implies it.
 *
 * Usage: render in the product-search no-result row, passing the current
 * search term and the surface ('catalog' | 'household').
 */

import type { CSSProperties } from 'react';
import { openReportProblemSheet } from './report-events';
import { captureScreenContext } from './screenContext';

interface ProductSearchReporterProps {
  searchTerm: string;
  surface: string;
}

const linkStyle: CSSProperties = {
  display: 'inline-block',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-accent-primary)',
  cursor: 'pointer',
  background: 'none',
  border: 'none',
  padding: 0,
  fontFamily: 'var(--font-family-sans)',
};

export function ProductSearchReporter({ searchTerm, surface }: ProductSearchReporterProps): JSX.Element {
  const handleClick = () => {
    const term = searchTerm.trim();
    openReportProblemSheet({
      itemContext: { kind: 'productsearch', searchTerm: term, surface },
      screenContext: captureScreenContext(),
      note: term,
    });
  };

  return (
    <button
      type="button"
      style={linkStyle}
      onClick={handleClick}
      data-testid="product-search-reporter"
    >
      Couldn&apos;t find a product? Tell us ›
    </button>
  );
}
